import apiClient from './api';
import type { ChatMessage, ChatSession, ChatResponse } from '../types';

export interface SendMessageParams {
  message: string;
  sessionId?: string;
}

export interface CreateSessionParams {
  title?: string;
}

/**
 * Send a message to the chatbot and get AI response
 */
export const sendChatMessage = async (params: SendMessageParams): Promise<ChatResponse> => {
  const response = await apiClient.post<{ success: boolean; data: ChatResponse }>('/chat/messages', params);
  return response.data.data;
};

/**
 * Get chat history for a specific session
 */
export const getChatHistory = async (sessionId: string): Promise<ChatMessage[]> => {
  const response = await apiClient.get<{ success: boolean; data: ChatMessage[] }>(`/chat/sessions/${sessionId}/messages`);
  return response.data.data;
};

/**
 * Create a new chat session
 */
export const createChatSession = async (params: CreateSessionParams = {}): Promise<ChatSession> => {
  const response = await apiClient.post<{ success: boolean; data: ChatSession }>('/chat/sessions', params);
  return response.data.data;
};

/**
 * List all chat sessions for the current user
 */
export const listChatSessions = async (): Promise<ChatSession[]> => {
  const response = await apiClient.get<{ success: boolean; data: ChatSession[] }>('/chat/sessions');
  return response.data.data;
};

/**
 * Get a specific chat session by ID
 */
export const getChatSession = async (sessionId: string): Promise<ChatSession> => {
  const response = await apiClient.get<{ success: boolean; data: ChatSession }>(`/chat/sessions/${sessionId}`);
  return response.data.data;
};

/**
 * Update a chat session (e.g., change title)
 */
export const updateChatSession = async (
  sessionId: string,
  updates: { title?: string; is_active?: boolean }
): Promise<ChatSession> => {
  const response = await apiClient.patch<{ success: boolean; data: ChatSession }>(`/chat/sessions/${sessionId}`, updates);
  return response.data.data;
};

/**
 * Delete a chat session
 */
export const deleteChatSession = async (sessionId: string): Promise<void> => {
  await apiClient.delete(`/chat/sessions/${sessionId}`);
};

/**
 * Send message with streaming support (for real-time responses)
 * Returns a ReadableStream that emits chunks of the response
 */
export const sendChatMessageStream = async (
  params: SendMessageParams
): Promise<ReadableStream<Uint8Array>> => {
  const response = await fetch(`${import.meta.env.VITE_API_URL}/chat/messages/stream`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem('supabase.auth.token')}`,
    },
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  if (!response.body) {
    throw new Error('Response body is null');
  }

  return response.body;
};
